import { apiClient } from '../client'

/**
 * Authentication-related types
 */
export interface LoginCredentials {
  email: string
  password: string
}

export interface TokenResponse {
  access: string
  refresh: string
}

export interface SignUpRequest {
  email: string
  password: string
  first_name: string
  last_name: string
}

export interface SignUpResponse {
  id: number
  email: string
  first_name: string
  last_name: string
}

export interface ForgotPasswordRequest {
  email: string
}

export interface VerifyEmailRequest {
  token: string
}

export interface SetNewPasswordRequest {
  token: string
  password: string
  password_confirm: string
}

export interface GoogleOAuthRequest {
  code: string
  redirect_uri: string
}

export interface LogoutRequest {
  fcm_token?: string | null
}

export interface ChangePasswordRequest {
  old_password: string
  new_password: string
}

/**
 * Authentication API endpoints
 */
export const authApi = {
  /**
   * User login with email and password
   */
  login: async (credentials: LoginCredentials): Promise<TokenResponse | unknown> => {
    const { data } = await apiClient.post('/auth/jwt/create/', credentials, {
      withCredentials: true,
    })
    return data
  },

  /**
   * User sign up / registration
   */
  signUp: async (payload: SignUpRequest): Promise<SignUpResponse> => {
    const { data } = await apiClient.post('/auth/users/', payload)
    return data
  },

  /**
   * Request password reset email
   */
  forgotPassword: async (payload: ForgotPasswordRequest): Promise<{ message: string }> => {
    const { data } = await apiClient.post('/auth/users/reset_password/', payload)
    return data
  },

  /**
   * Verify email address with token
   */
  verifyEmail: async (payload: VerifyEmailRequest): Promise<unknown> => {
    const { data } = await apiClient.post('/auth/users/activation/', payload)
    return data
  },

  /**
   * Set new password after forgot password flow
   */
  setNewPassword: async (payload: SetNewPasswordRequest): Promise<{ message: string }> => {
    const { data } = await apiClient.post('/auth/users/reset_password_confirm/', payload)
    return data
  },

  /**
   * Google OAuth authentication
   */
  googleOAuth: async (payload: GoogleOAuthRequest): Promise<TokenResponse> => {
    const { data } = await apiClient.post('/auth/jwt/google-oauth/', payload, {
      withCredentials: true,
    })
    return data
  },

  /**
   * Get Google OAuth URL for initiating OAuth flow
   */
  getGoogleOAuthUrl: async (redirectUri: string): Promise<{ authorization_url: string }> => {
    const { data } = await apiClient.get(`/auth/o/google-oauth2/?redirect_uri=${redirectUri}`)
    return data
  },

  /**
   * Complete Google OAuth registration
   */
  googleOAuthRegister: async (params: string, payload: { state: string; code: string }): Promise<any> => {
    const { data } = await apiClient.post(`/auth/o/google-oauth2/?${params}`, payload, {
      withCredentials: true,
    })
    return data
  },

  /**
   * User logout
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/jwt/logout/')
  },

  /**
   * Change user password
   */
  changePassword: async (payload: ChangePasswordRequest): Promise<{ message: string }> => {
    const { data } = await apiClient.post('/auth/users/set_password/', payload)
    return data
  },

  /**
   * Get user permissions
   */
  getUserPermissions: async (): Promise<string[]> => {
    const { data } = await apiClient.get('/auth/users/me/permissions/')
    return data
  },
}
